import { sha256 } from "@noble/hashes/sha2.js";
import { bytesToHex } from "@noble/hashes/utils.js";
import { connectorsByIdentifier } from "../connectors";
import { resolveAuth } from "../connectors/resolveAuth";
import destinationPolicyManifest from "./destinationPolicyManifest.json";

export type CredentialPolicyConnector = {
  tenantId: string;
  identifier: string;
  urlPattern: string;
  configuration: Record<string, string>;
  backingWorkflowId?: string | null;
  authorizationMode?: string | null;
};

export const DESTINATION_EDIT_WINDOW_MS = 60 * 1000;

type DestinationPolicyManifest = {
  policyId: string;
  boundRoutedConfigurationFields: Record<string, string[]>;
  digestRoutedConfigurationFields: string[];
  boundRoutedAuthParams: Record<string, string[]>;
  destinationIndependentSecretFields: Record<string, string[]>;
};

const manifest: DestinationPolicyManifest = destinationPolicyManifest;

function tokenUrlConfigurationFields(
  descriptor: ReturnType<typeof resolveAuth>
): string[] | null {
  if (descriptor.type !== "primitive" || descriptor.name !== "fetchToken")
    return null;
  const request = descriptor.params?.request;
  if (!request || typeof request !== "object" || Array.isArray(request))
    return null;
  const url = (request as Record<string, unknown>).url;
  if (!url || typeof url !== "object" || Array.isArray(url)) return null;
  const urlRef = url as Record<string, unknown>;
  if (urlRef.from === "literal") return [];
  if (urlRef.from === "config" && typeof urlRef.field === "string")
    return [urlRef.field];
  if (urlRef.from !== "template" || typeof urlRef.template !== "string")
    return null;
  const template = urlRef.template;
  if (/\{(?:secrets|oauth)\./.test(template)) return null;
  return Array.from(
    template.matchAll(/\{configuration\.(\w+)\}/g),
    (match) => match[1]!
  ).sort();
}

function selectedConfiguration(
  configuration: Record<string, string>,
  fields: string[],
  digestFields?: string[]
): Record<string, string> {
  return Object.fromEntries(
    fields
      .filter((field) => Object.hasOwn(configuration, field))
      .map((field) => [
        field,
        digestFields?.includes(field)
          ? policyDigest(configuration[field]!)
          : configuration[field]!,
      ])
  );
}

function policyDigest(value: string): string {
  return `sha256:${bytesToHex(sha256(new TextEncoder().encode(value)))}`;
}

function tokenRequestAuthPolicy(auth: unknown) {
  if (auth === undefined) return null;
  if (
    !auth ||
    typeof auth !== "object" ||
    Array.isArray(auth) ||
    (auth as Record<string, unknown>).type !== "primitive"
  )
    throw new Error("Unsupported token request auth in credential policy");
  const descriptor = auth as Record<string, unknown>;
  if (descriptor.name === "loginCredentials") return descriptor;
  if (descriptor.name === "awsRole" || descriptor.name === "awsSigV4")
    return { type: "primitive", name: descriptor.name };
  throw new Error("Unsupported token request auth in credential policy");
}

function routedAuthPolicy(
  descriptor: ReturnType<typeof resolveAuth>,
  boundRoutedAuthParams: Record<string, string[]>
) {
  if (descriptor.type !== "primitive") return descriptor;
  const bound = new Set(boundRoutedAuthParams[descriptor.name] ?? []);
  return {
    type: descriptor.type,
    name: descriptor.name,
    params: Object.fromEntries(
      Object.entries(descriptor.params ?? {}).filter(([field]) =>
        bound.has(field)
      )
    ),
  };
}

const RUNTIME_AUTH_IDENTIFIERS = new Set([
  "workflowApp",
  "awsEks",
  "awsRdsMysql",
  "awsRdsPostgres",
]);

export function canBindCredential(app: CredentialPolicyConnector): boolean {
  return (
    !app.backingWorkflowId &&
    app.authorizationMode !== "visitor" &&
    !RUNTIME_AUTH_IDENTIFIERS.has(app.identifier) &&
    connectorsByIdentifier[app.identifier] !== undefined
  );
}

export function credentialPolicy(
  app: CredentialPolicyConnector,
  field: string
): string {
  const definition = connectorsByIdentifier[app.identifier];
  if (!definition) throw new Error(`Unknown connector: ${app.identifier}`);
  const base = {
    policyId: manifest.policyId,
    tenantId: app.tenantId,
    identifier: app.identifier,
    field,
  };
  if (
    manifest.destinationIndependentSecretFields[app.identifier]?.includes(field)
  )
    return JSON.stringify({ ...base, destination: "field-only" });
  const authDescriptor = resolveAuth(definition.auth, {
    id: "",
    configuration: app.configuration,
    secrets: {},
    oauth: null,
  });
  const tokenUrlFields = tokenUrlConfigurationFields(authDescriptor);
  const tokenRequest =
    authDescriptor.type === "primitive" && authDescriptor.name === "fetchToken"
      ? authDescriptor.params?.request
      : null;
  const tokenRequestAuth = tokenRequestAuthPolicy(
    tokenRequest && typeof tokenRequest === "object"
      ? (tokenRequest as Record<string, unknown>).auth
      : undefined
  );
  if (
    authDescriptor.type === "primitive" &&
    authDescriptor.name === "fetchToken" &&
    tokenUrlFields === null
  )
    throw new Error("Unsupported token URL in credential policy");
  const routed =
    definition.protocol !== "http" ||
    (authDescriptor.type === "primitive" && tokenUrlFields === null);
  const routedFields = manifest.boundRoutedConfigurationFields[app.identifier];
  if (routed && routedFields === undefined)
    throw new Error(`Missing destination policy for ${app.identifier}`);
  if (
    routed &&
    authDescriptor.type === "primitive" &&
    Object.keys(authDescriptor.params ?? {}).length > 0 &&
    manifest.boundRoutedAuthParams[authDescriptor.name] === undefined
  )
    throw new Error(`Missing destination policy for ${authDescriptor.name}`);
  return JSON.stringify({
    ...base,
    destination: {
      protocol: definition.protocol,
      urlPattern: app.urlPattern,
      caCertificate:
        app.configuration.caCertificate === undefined
          ? null
          : policyDigest(app.configuration.caCertificate),
      ...(definition.protocol === "http" && authDescriptor.type !== "primitive"
        ? { auth: "direct" }
        : definition.protocol === "http" && tokenUrlFields !== null
          ? {
              auth: "fetchToken",
              configuration: selectedConfiguration(
                app.configuration,
                tokenUrlFields
              ),
              tokenUrl: (tokenRequest as Record<string, unknown>).url,
              tokenRequestAuth,
            }
          : {
              auth: "routed",
              configuration: selectedConfiguration(
                app.configuration,
                routedFields!,
                manifest.digestRoutedConfigurationFields
              ),
              authDescriptor: routedAuthPolicy(
                authDescriptor,
                manifest.boundRoutedAuthParams
              ),
            }),
    },
  });
}
